import React, { useState, useEffect } from 'react';
import {
  Box, Card, Typography, TextField, MenuItem, InputAdornment,
  IconButton, Tooltip, Button, TableContainer, Paper, Table,
  TableHead, TableRow, TableCell, TableBody, TablePagination,
  CircularProgress, Chip, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import RestoreIcon from '@mui/icons-material/Restore';
import { useAuth } from '../../hooks/useAuth';
import CommonLayerUpload from './CommonLayerUpload';
import FitmentUpload from './FitmentUpload';
import UpdateRequestDialog from './UpdateRequestDialog';
import AssignToL2, { AssignToL2Data } from './AssignToL2';
import { useQuery, gql, useMutation } from '@apollo/client';
import { useSnackbar } from 'notistack';
import * as XLSX from 'xlsx';

// Define the GraphQL queries and mutations as before
const GET_ALL_AIS_KYC_REQUESTS = gql`
  query GetAllAisKycRequests($offset: Int, $limit: Int, $search: String, $status: String) {
    getAllAisKycRequests(offset: $offset, limit: $limit, search: $search, status: $status) {
      success
      data {
        kyc_id
        request_id
        raised_date
        vin
        project_name
        request_type
        device_model
        device_serial_number
        device_imei_number
        engine_number
        iccid_number
        customer_name
        customer_number
        customer_otp_number
        customer_address
        vehicle_model
        registration_state
        rto_name
        rto_number
        dealer_name
        dealer_contact_number
        alternate_dealer_name
        alternate_dealer_number
        sim_activation_status
        msisdn_1
        msisdn_2
        vehicle_type
        year_of_manufacture
        comman_layer_cert
        common_layer_cert_issued_date
        fitment_cert
        fitment_cert_issued_date
        status
        reason
        followup
        parked_by
        otp_sent
      }
      count
      error
    }
  }
`;

// Monitor badges (New / In Progress / Parked / Closed today).
const GET_AIS_KYC_STATS = gql`
  query AisKycStats {
    aisKycStats {
      newCount
      inProgressCount
      parkedCount
      closedTodayCount
    }
  }
`;

// Queue actions -- same kyc_details queue as the partner portal.
const PARK_AIS_KYC_REQUEST = gql`
  mutation ParkAisKycRequest($id: String!, $reason: String!, $followUpDate: String!) {
    parkAisKycRequest(id: $id, reason: $reason, followUpDate: $followUpDate) {
      success
      message
      error
    }
  }
`;

const UNPARK_AIS_KYC_REQUEST = gql`
  mutation UnparkAisKycRequest($id: String!) {
    unparkAisKycRequest(id: $id) {
      success
      message
      error
    }
  }
`;

const CLOSE_AIS_KYC_REQUEST = gql`
  mutation CloseAisKycRequest($id: String!) {
    closeAisKycRequest(id: $id) {
      success
      message
      error
    }
  }
`;

/**
 * Undo an accidental Close, putting the request back in the live ('New') queue.
 *
 * SUPER_ADMIN only — the server checks the role itself and refuses everyone
 * else, including the KYC_DETAILS update_access that Park/Unpark/Close use.
 * Those are the ordinary queue actions; this corrects a mistake made with them.
 *
 * Refuses when the request was closed by its KYC DECISION (approved/rejected)
 * rather than by the queue. The status column reads 'Closed' for both and
 * cannot tell them apart, so the server's explanation is shown as-is.
 */
const REOPEN_AIS_KYC_REQUEST = gql`
  mutation ReopenAisKycRequest($id: String!) {
    reopenAisKycRequest(id: $id) {
      success
      message
      error
    }
  }
`;

// Park-reason options mirror the partner portal's console.
const PARK_REASONS = [
  'Customer not reachable',
  'Pending payment confirmation',
  'Device issue — awaiting replacement',
  'RTO query pending',
  'Other',
];

const UPLOAD_PDF_MUTATION = gql`
  mutation UploadPdf($pdf: Upload!) {
    uploadPdf(pdf: $pdf) {
      success
      url
      filename
      error
      originalName
      size
      mimetype
    }
  }
`;

const UPLOAD_CERTIFICATE_MUTATION = gql`
  mutation UploadCertificate($input: CertificateUploadInput!) {
    uploadCertificate(input: $input) {
      success
      message
      error
    }
  }
`;

// Define the types as before
type AisKycRequest = {
  kyc_id: string;
  request_id: string;
  raised_date: string;
  vin: string;
  project_name: string;
  // "AIS-140" | "Top-Up", derived server-side from whether a project name was
  // captured. Not stored, and not the same thing as kyc_mode.
  request_type: string;
  device_model: string;
  device_serial_number: string;
  device_imei_number: string;
  engine_number: string;
  iccid_number: string;
  customer_name: string;
  customer_number: string;
  customer_otp_number: string;
  customer_address: string;
  vehicle_model: string;
  registration_state: string;
  rto_name: string;
  rto_number: string;
  dealer_name: string;
  dealer_contact_number: string;
  alternate_dealer_name: string;
  alternate_dealer_number: string;
  sim_activation_status: string;
  msisdn_1: string;
  msisdn_2: string;
  vehicle_type: string;
  year_of_manufacture: number;
  comman_layer_cert: string | null;
  common_layer_cert_issued_date: number | null;
  fitment_cert: string | null;
  fitment_cert_issued_date: number | null;
  status: string | null;
  reason: string | null;
  followup: string | null;
  parked_by: string | null;
  otp_sent: string | null;
};

type Certificate = AisKycRequest & {
  id: number;
  commonLayerUpload: string;
  fitmentUpload: string;
  commonLayerDownload: string;
  inProgressDate: string;
  paymentStatus: string;
  requestStatus: string;
  otp: string;
  otpSentOn: string;
  reason: string;
  telematicsCheck: string;
  assignToL2: string;
};

type GetAllAisKycRequestsResponse = {
  getAllAisKycRequests: {
    success: boolean;
    data: AisKycRequest[];
    count: number;
    error: string | null;
  };
};

// Helper function to convert epoch time to human-readable date
const formatDateFromEpoch = (value: string | number | null): string => {
  if (typeof value === 'number') {
    const date = new Date(value * 1000);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  return value || "N/A";
};

const CertificateManager = () => {
  const { enqueueSnackbar } = useSnackbar();
  // Reopen is the one action here the server gates on the ROLE rather than on
  // KYC_DETAILS update_access, so it cannot go through canUpdate like the rest.
  const { isSuperAdmin } = useAuth();
  const canReopen = isSuperAdmin();
  const [searchType, setSearchType] = useState('vin');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [commonLayerDialogOpen, setCommonLayerDialogOpen] = useState(false);
  const [fitmentDialogOpen, setFitmentDialogOpen] = useState(false);
  const [selectedCertificate, setSelectedCertificate] = useState<Certificate | null>(null);
  const [assignToL2DialogOpen, setAssignToL2DialogOpen] = useState(false);
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [activeSubTab, setActiveSubTab] = useState<'live' | 'parked' | 'closed'>('live');
  // Park dialog
  const [parkDialogOpen, setParkDialogOpen] = useState(false);
  const [parkTarget, setParkTarget] = useState<Certificate | null>(null);
  const [parkReason, setParkReason] = useState('');
  const [parkFollowUpDate, setParkFollowUpDate] = useState('');
  const [actionBusy, setActionBusy] = useState(false);

  // Each tab is a server-side status filter: Live = the working inbox
  // (New + In Progress), Parked and Closed are exact queue states.
  const statusFilter =
    activeSubTab === 'live' ? 'Inbound' : activeSubTab === 'parked' ? 'Parked' : 'Closed';

  // Debounce the search box so we don't refetch on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 400);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Reset to the first page whenever the filter or search changes.
  useEffect(() => {
    setPage(0);
  }, [statusFilter, debouncedSearch]);

  const { data, loading, error, refetch } = useQuery<GetAllAisKycRequestsResponse>(
    GET_ALL_AIS_KYC_REQUESTS,
    {
      variables: {
        offset: page * rowsPerPage,
        limit: rowsPerPage,
        search: debouncedSearch || undefined,
        status: statusFilter,
      },
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
    }
  );

  const { data: statsData, refetch: refetchStats } = useQuery(GET_AIS_KYC_STATS, {
    fetchPolicy: 'network-only',
  });
  const stats = statsData?.aisKycStats;

  const totalCount = data?.getAllAisKycRequests?.count ?? 0;

  const tableData: Certificate[] = (data?.getAllAisKycRequests?.data ?? []).map(
    (item: AisKycRequest, index: number) => ({
      id: index + 1 + page * rowsPerPage,
      ...item,
      commonLayerUpload: '',
      fitmentUpload: '',
      commonLayerDownload: item.comman_layer_cert || 'N/A',
      inProgressDate: '',
      paymentStatus: '',
      requestStatus: item.status || '',
      otp: '',
      otpSentOn: item.otp_sent || '',
      reason: item.reason || '',
      telematicsCheck: '',
      assignToL2: '',
    })
  );
  const [uploadPdf] = useMutation(UPLOAD_PDF_MUTATION);
  const [uploadCertificate] = useMutation(UPLOAD_CERTIFICATE_MUTATION);
  const [parkAisKycRequest] = useMutation(PARK_AIS_KYC_REQUEST);
  const [unparkAisKycRequest] = useMutation(UNPARK_AIS_KYC_REQUEST);
  const [closeAisKycRequest] = useMutation(CLOSE_AIS_KYC_REQUEST);
  const [reopenAisKycRequest] = useMutation(REOPEN_AIS_KYC_REQUEST);

  // The queue identifies a request by its ticket (request_id), falling back to
  // the raw kyc_id -- exactly what the backend matches on.
  const requestKey = (c: Certificate) => c.request_id || c.kyc_id;

  const refreshQueue = () => {
    refetch();
    refetchStats();
  };

  const openParkDialog = (certificate: Certificate) => {
    setParkTarget(certificate);
    setParkReason('');
    setParkFollowUpDate('');
    setParkDialogOpen(true);
  };

  const handleParkSubmit = async () => {
    if (!parkTarget) return;
    if (!parkReason || !parkFollowUpDate) {
      enqueueSnackbar('Pick a reason and a follow-up date to park this request.', { variant: 'warning' });
      return;
    }
    setActionBusy(true);
    try {
      const res = await parkAisKycRequest({
        variables: { id: requestKey(parkTarget), reason: parkReason, followUpDate: parkFollowUpDate },
      });
      const r = res.data?.parkAisKycRequest;
      if (r?.success) {
        enqueueSnackbar(r.message || 'Request parked.', { variant: 'success' });
        setParkDialogOpen(false);
        setParkTarget(null);
        refreshQueue();
      } else {
        enqueueSnackbar(r?.error || 'Failed to park request.', { variant: 'error' });
      }
    } catch (e: any) {
      enqueueSnackbar(e.message || 'Failed to park request.', { variant: 'error' });
    } finally {
      setActionBusy(false);
    }
  };

  const handleUnpark = async (certificate: Certificate) => {
    setActionBusy(true);
    try {
      const res = await unparkAisKycRequest({ variables: { id: requestKey(certificate) } });
      const r = res.data?.unparkAisKycRequest;
      if (r?.success) {
        enqueueSnackbar(r.message || 'Request unparked.', { variant: 'success' });
        refreshQueue();
      } else {
        enqueueSnackbar(r?.error || 'Failed to unpark request.', { variant: 'error' });
      }
    } catch (e: any) {
      enqueueSnackbar(e.message || 'Failed to unpark request.', { variant: 'error' });
    } finally {
      setActionBusy(false);
    }
  };

  /**
   * Put an accidentally-closed request back in the live queue.
   *
   * A `success: false` here is usually not a failure to fix: the common case is
   * a request closed by its KYC decision rather than by the queue, which is not
   * undoable this way. The server says which, so its message is shown unchanged
   * instead of being replaced by a generic one.
   */
  const handleReopen = async (certificate: Certificate) => {
    setActionBusy(true);
    try {
      const res = await reopenAisKycRequest({ variables: { id: requestKey(certificate) } });
      const r = res.data?.reopenAisKycRequest;
      if (r?.success) {
        enqueueSnackbar(r.message || 'Request reopened.', { variant: 'success' });
        // It leaves the Closed tab for the Live one, so both the rows and the
        // queue badges have to come back.
        refreshQueue();
      } else {
        enqueueSnackbar(r?.error || 'Failed to reopen request.', { variant: 'error' });
      }
    } catch (e: any) {
      enqueueSnackbar(e.message || 'Failed to reopen request.', { variant: 'error' });
    } finally {
      setActionBusy(false);
    }
  };

  const handleClose = async (certificate: Certificate) => {
    // The fitment cert auto-approves (and thus closes) a request server-side, so
    // closing without any cert is unusual -- confirm it.
    if (!certificate.comman_layer_cert && !certificate.fitment_cert) {
      if (!window.confirm('No certificates uploaded yet. Close this request anyway?')) return;
    }
    setActionBusy(true);
    try {
      const res = await closeAisKycRequest({ variables: { id: requestKey(certificate) } });
      const r = res.data?.closeAisKycRequest;
      if (r?.success) {
        enqueueSnackbar(r.message || 'Request closed.', { variant: 'success' });
        refreshQueue();
      } else {
        enqueueSnackbar(r?.error || 'Failed to close request.', { variant: 'error' });
      }
    } catch (e: any) {
      enqueueSnackbar(e.message || 'Failed to close request.', { variant: 'error' });
    } finally {
      setActionBusy(false);
    }
  };

  const tableColumns = [
    'Sr.No', 'Request ID', 'Raised Date', 'VIN', 'Common Layer Upload', 'Fitment Upload',
    // 'Common Layer Certificate Download', 'Fitment Certificate Download',
    'CLA Issued On', 'Fitment Issued On', 'Project Name', 'Request Type',
    'InProgress Date', 'Device Model', 'Device Serial Number', 'Device IMEI Number',
    'Engine Number', 'ICCID Number', 'Customer Name', 'Customer Number',
    'Customer OTP number', 'Customer Address', 'Vehicle Model', 'Registration State',
    'RTO Name', 'RTO Number', 'Dealer Name', 'Dealer Contact Number', 'Alternate Dealer Name',
    'Alternate Dealer Number', 'Payment Status', 'SIM Activation status', 'MSISDN1',
    'MSISDN2', 'Vehicle Type', 'Year Of Manufacture', 'Request Status', 'OTP',
    'OTP Sent On', 'Reason', 'Telematics Check', 'Assign To L2', 'Action'
  ];

  // Colour the status pill by queue state.
  const statusColor = (status?: string): string => {
    switch (status) {
      case 'New': return '#1976d2';
      case 'In Progress': return '#ed6c02';
      case 'Parked': return '#9c27b0';
      case 'Closed': return '#2e7d32';
      default: return '#9e9e9e';
    }
  };

  // AIS-140 vs Top-Up. Only two values, and the point of the column is to make
  // them separable at a glance, so they get contrasting pills rather than a
  // shared neutral one. Anything unexpected stays grey rather than being
  // silently coloured as one of the two.
  const requestTypeColor = (type?: string): string => {
    switch (type) {
      case 'AIS-140': return '#0288d1';
      case 'Top-Up': return '#7b1fa2';
      default: return '#9e9e9e';
    }
  };

  const cellStyle = {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    padding: { xs: '4px', sm: '6px' },
    fontSize: { xs: '0.75rem', sm: '0.85rem' },
    borderRight: '1px solid #e0e0e0',
  };

  const handleChangePage: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => void = (event, newPage) => {
    // Query variables are derived from state, so changing the page refetches.
    setPage(newPage);
  };

  interface ChangeRowsPerPageEvent extends React.ChangeEvent<HTMLInputElement> { }

  const handleChangeRowsPerPage = (event: ChangeRowsPerPageEvent) => {
    const rpp = parseInt(event.target.value, 10);
    setRowsPerPage(rpp);
    setPage(0);
  };

  const handleExportToExcel = () => {
    const formattedData = tableData.map((item) => ({
      'Request ID': item.request_id || "N/A",
      'VIN': item.vin || "N/A",
      'Raised Date': item.raised_date || "N/A",
      'Project Name': item.project_name || "N/A",
      'Request Type': item.request_type || "N/A",
      'Device Model': item.device_model || "N/A",
      'Device Serial Number': item.device_serial_number || "N/A",
      'Device IMEI Number': item.device_imei_number || "N/A",
      'Engine Number': item.engine_number || "N/A",
      'ICCID Number': item.iccid_number || "N/A",
      'Customer Name': item.customer_name || "N/A",
      'Customer Number': item.customer_number || "N/A",
      'Customer OTP number': item.customer_otp_number || "N/A",
      'Customer Address': item.customer_address || "N/A",
      'Vehicle Model': item.vehicle_model || "N/A",
      'Registration State': item.registration_state || "N/A",
      'RTO Name': item.rto_name || "N/A",
      'RTO Number': item.rto_number || "N/A",
      'Dealer Name': item.dealer_name || "N/A",
      'Dealer Contact Number': item.dealer_contact_number || "N/A",
      'Alternate Dealer Name': item.alternate_dealer_name || "N/A",
      'Alternate Dealer Number': item.alternate_dealer_number || "N/A",
      'Payment Status': item.paymentStatus || "N/A",
      'SIM Activation status': item.sim_activation_status || "N/A",
      'MSISDN1': item.msisdn_1 || "N/A",
      'MSISDN2': item.msisdn_2 || "N/A",
      'Vehicle Type': item.vehicle_type || "N/A",
      'Year Of Manufacture': item.year_of_manufacture || "N/A",
      'Request Status': item.requestStatus || "N/A",
      'OTP': item.otp || "N/A",
      'OTP Sent On': item.otpSentOn || "N/A",
      'Reason': item.reason || "N/A",
      'CLA Issued On': item.common_layer_cert_issued_date ? formatDateFromEpoch(item.common_layer_cert_issued_date) : "N/A",
      'Fitment Issued On': item.fitment_cert_issued_date ? formatDateFromEpoch(item.fitment_cert_issued_date) : "N/A",
    }));
    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Certificates');
    XLSX.writeFile(workbook, 'certificates.xlsx');

  };

  const handleButtonClick = (column: string, certificate: Certificate) => {
    if (column === 'Assign To L2') {
      setSelectedCertificate(certificate);
      setAssignToL2DialogOpen(true);
    } else if (column === 'Telematics Check') {
      console.log('Telematics Check clicked', certificate.id);
    }
  };

  const handleAssignToL2DialogClose = () => {
    setAssignToL2DialogOpen(false);
    setSelectedCertificate(null);
  };

  const handleAssignToL2Submit = (data: AssignToL2Data) => {
    console.log('Assign to L2 submitted:', data, selectedCertificate);
    setAssignToL2DialogOpen(false);
    setSelectedCertificate(null);
  };

  const handleUploadClick = (column: string, certificate: Certificate) => {
    setSelectedCertificate(certificate);
    if (column === 'Common Layer Upload') {
      setCommonLayerDialogOpen(true);
    } else if (column === 'Fitment Upload') {
      setFitmentDialogOpen(true);
    }
  };

  const handleUploadPdf = async (file: File): Promise<{ success: boolean; url?: string; error?: string }> => {
    try {
      const result = await uploadPdf({
        variables: { pdf: file }
      });
      return result.data.uploadPdf;
    } catch (error: any) {
      console.error('Error uploading PDF:', error);
      return { success: false, error: error.message };
    }
  };

  const handleUploadCertificate = async (input: {
    url: string;
    kyc_id: string;
    document_type: string;
  }): Promise<{ success: boolean; message?: string; error?: string }> => {
    try {
      const result = await uploadCertificate({
        variables: { input }
      });
      return result.data.uploadCertificate;
    } catch (error: any) {
      console.error('Error uploading certificate:', error);
      return { success: false, error: error.message };
    }
  };

  const handleCommonLayerFileUpload = async (file: File) => {
    if (selectedCertificate) {
      try {
        console.log("File upload completed:", file.name);
        refreshQueue();
      } catch (error) {
        console.error("Error handling common layer file upload:", error);
      }
    }
  };

  const handleFitmentFileUpload = async (file: File) => {
    if (selectedCertificate) {
      try {
        console.log("File upload completed:", file.name);
        // The fitment cert auto-approves the KYC, so the row moves to Closed --
        // refresh the list and the monitor badges to reflect it.
        refreshQueue();
      } catch (error) {
        console.error("Error handling fitment file upload:", error);
      }
    }
  };

  const handleCloseCommonLayerDialog = () => {
    setCommonLayerDialogOpen(false);
    setSelectedCertificate(null);
  };

  const handleCloseFitmentDialog = () => {
    setFitmentDialogOpen(false);
    setSelectedCertificate(null);
  };


  // Search and pagination are both server-side now; render the page as-is.
  const paginatedData = tableData;


  const handleUpdateDialogClose = () => {
    setUpdateDialogOpen(false);
    setSelectedCertificate(null);
  };

  const handleUpdateRequestSubmit = (formData: { otp: string; reason: string; status: string }) => {
    console.log("Update Request Data:", {
      requestId: selectedCertificate?.request_id,
      vin: selectedCertificate?.vin,
      ...formData,
    });
    // TODO: Call GraphQL mutation to update
    setUpdateDialogOpen(false);
  };

  const subTabStyle = (isActive: boolean) => ({
    fontWeight: 600,
    fontSize: '1rem',
    px: 3,
    py: 1.5,
    borderRadius: '20px',
    textTransform: 'none' as const,
    cursor: 'pointer',
    transition: 'all 0.25s ease',
    color: isActive ? '#fff' : '#555',
    backgroundColor: isActive ? '#E60012' : 'transparent',
    '&:hover': {
      backgroundColor: isActive ? '#E60012' : '#ffffffc0',
    },
    minWidth: 'auto',
  });

  return (
    <Box sx={{ width: '100%', px: { xs: 0, sm: 0 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        {/* <Typography variant="h5" fontSize={{ xs: '1.5rem', sm: '2.125rem' }} sx={{ borderRadius: 2 }}>
          Certificate Manager
        </Typography> */}
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            backgroundColor: '#f0f0f0',
            borderRadius: '24px',
            padding: '4px',
            gap: '4px',
          }}
        >
          <Button
            disableRipple
            onClick={() => setActiveSubTab('live')}
            sx={subTabStyle(activeSubTab === 'live')}
          >
            Live Request
          </Button>
          <Button
            disableRipple
            onClick={() => setActiveSubTab('parked')}
            sx={subTabStyle(activeSubTab === 'parked')}
          >
            Parked Request
          </Button>
          <Button
            disableRipple
            onClick={() => setActiveSubTab('closed')}
            sx={subTabStyle(activeSubTab === 'closed')}
          >
            Closed Request
          </Button>
        </Box>

        {/* Monitor: live queue badges */}
        {/* <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          {[
            { label: 'New', value: stats?.newCount, color: '#1976d2' },
            { label: 'In Progress', value: stats?.inProgressCount, color: '#ed6c02' },
            { label: 'Parked', value: stats?.parkedCount, color: '#9c27b0' },
            { label: 'Closed today', value: stats?.closedTodayCount, color: '#2e7d32' },
          ].map((s) => (
            <Box
              key={s.label}
              sx={{
                minWidth: 92,
                px: 2,
                py: 1,
                borderRadius: 2,
                backgroundColor: '#fff',
                border: '1px solid #e0e0e0',
                borderTop: `3px solid ${s.color}`,
                boxShadow: '0 2px 6px -4px #b0b0b0',
                textAlign: 'center',
              }}
            >
              <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: s.color, lineHeight: 1 }}>
                {s.value ?? '—'}
              </Typography>
              <Typography sx={{ fontSize: '0.7rem', color: '#666', mt: 0.5 }}>{s.label}</Typography>
            </Box>
          ))}
        </Box> */}
      </Box>
      <Card sx={{
        width: '100%',
        maxWidth: '100vw',
        mx: 'auto',
        boxShadow: 2,
        borderRadius: 3,
        p: 0,
        display: 'flex',
        flexDirection: 'column',
        height: { xs: 'auto', md: 'calc(93vh - 120px)' },
        bgcolor: '#fff',
        minHeight: 100,
      }}>
        {/* Sticky header controls */}
        <Box sx={{
          position: 'sticky',
          top: 0,
          zIndex: 20,
          background: '#fff',
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
          boxShadow: '0 2px 8px -4px #e0e0e0',
          px: { xs: 1, sm: 2 },
          pt: 2,
          pb: 2,
          left: 0,
          right: 0,
          width: '100%',
          minWidth: 0,
          overflow: 'visible',
        }}>
          <Box
            display="flex"
            flexDirection={{ xs: 'column', sm: 'row' }}
            sx={{ justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 2 }}
          >
            <Box
              display="flex"
              gap={1}
              alignItems="center"
              sx={{ minWidth: 0, flex: 1, maxWidth: { xs: '100%', sm: '40%' }, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}
            >
              <TextField
                select
                size="small"
                value={searchType}
                onChange={(e) => setSearchType(e.target.value)}
                sx={{ minWidth: 120, background: '#f5f5f5', flex: '0 0 auto' }}
              >
                <MenuItem value="vin">Search by VIN</MenuItem>
                <MenuItem value="requestId">Search by Request ID</MenuItem>
              </TextField>
              <TextField
                size="small"
                placeholder={
                  searchType === 'vin' ? 'Enter VIN' :
                    searchType === 'requestId' ? 'Enter Request ID' : ''
                }
                variant="outlined"
                sx={{ minWidth: 120, background: '#f5f5f5', flex: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <IconButton sx={{ ml: 1, color: '#000', flex: '0 0 auto' }} onClick={handleExportToExcel}>
                <Tooltip title="Export to Excel" placement="top" arrow>
                  <CloudDownloadIcon />
                </Tooltip>
              </IconButton>
            </Box>
          </Box>
        </Box>

        {/* Table area */}
        <Box sx={{
          flex: 1,
          minHeight: 0,
          px: { xs: 0.5, sm: 2 },
          py: 2,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}>
          <Box sx={{ width: '100%', overflowX: 'auto' }}>
            <TableContainer
              component={Paper}
              sx={{
                boxShadow: 'none',
                borderRadius: 2,
                width: '100%',
                minWidth: 600,
                background: '#fff',
                overflow: 'visible',
              }}
            >
              <Table stickyHeader size="small" sx={{ minWidth: 600, width: '100%', tableLayout: 'auto' }}>
                <TableHead>
                  <TableRow sx={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: '#E60012' }}>
                    {tableColumns.map((header) => (
                      <TableCell
                        key={header}
                        align="center"
                        sx={{
                          backgroundColor: '#E60012',
                          color: '#fff',
                          fontWeight: 'bold',
                          fontSize: { xs: '0.75rem', sm: '0.85rem' },
                          whiteSpace: 'nowrap',
                          padding: { xs: '6px', sm: '8px' },
                          borderBottom: '1px solid #e0e0e0',
                          borderRight: '2px solid #e0e0e0',
                          boxShadow: '0 2px 8px -4px #e0e0e0',
                          position: 'sticky',
                          top: 0,
                          zIndex: 11,
                        }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={tableColumns.length} align="center" sx={{ py: 4 }}>
                        <CircularProgress size={24} />
                      </TableCell>
                    </TableRow>
                  ) : paginatedData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={tableColumns.length} align="center" sx={{ fontSize: '1.1rem', color: '#888', py: 4 }}>
                        No certificates found
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedData.map((certificate, idx) => (
                      <TableRow
                        key={certificate.id}
                        hover
                        sx={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#f5f5f5' }}
                      >
                        <TableCell align="center" sx={cellStyle}>{page * rowsPerPage + idx + 1}</TableCell>
                        <TableCell
                          align="center"
                          sx={cellStyle}
                          onClick={() => {
                            setSelectedCertificate(certificate);
                            setUpdateDialogOpen(true);
                          }}
                          style={{ cursor: "pointer", color: "green", textDecoration: "underline" }}
                        >
                          {certificate.request_id || "N/A"}
                        </TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.raised_date || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.vin || "N/A"}</TableCell>
                        {/* Common Layer Upload button */}
                        <TableCell align="center" sx={cellStyle}>
                          <Button
                            variant="contained"
                            onClick={() => handleUploadClick('Common Layer Upload', certificate)}
                            disabled={!!certificate.comman_layer_cert}
                            sx={{
                              backgroundColor: !!certificate.comman_layer_cert ? '#f0f0f0' : '#58B80F1A',
                              color: !!certificate.comman_layer_cert ? '#888' : '#58B80F',
                              borderRadius: '20px',
                              height: '24px',
                              padding: '0 12px',
                              fontSize: '12px',
                              fontWeight: 'normal',
                              textTransform: 'none',
                              boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                              '&:hover': {
                                backgroundColor: !!certificate.comman_layer_cert ? '#f0f0f0' : '#58B80F33',
                              },
                              border: `1px solid ${!!certificate.comman_layer_cert ? '#e0e0e0' : '#58B80F'}`,
                              minWidth: '50px',
                            }}
                          >
                            <CloudUploadIcon fontSize="small" />
                          </Button>
                        </TableCell>
                        {/* Fitment Upload button */}
                        <TableCell align="center" sx={cellStyle}>
                          <Button
                            variant="contained"
                            onClick={() => handleUploadClick('Fitment Upload', certificate)}
                            disabled={!!certificate.fitment_cert}
                            sx={{
                              backgroundColor: !!certificate.fitment_cert ? '#f0f0f0' : '#58B80F1A',
                              color: !!certificate.fitment_cert ? '#888' : '#58B80F',
                              borderRadius: '20px',
                              height: '24px',
                              padding: '0 12px',
                              fontSize: '12px',
                              fontWeight: 'normal',
                              textTransform: 'none',
                              boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                              '&:hover': {
                                backgroundColor: !!certificate.fitment_cert ? '#f0f0f0' : '#58B80F33',
                              },
                              border: `1px solid ${!!certificate.fitment_cert ? '#e0e0e0' : '#58B80F'}`,
                              minWidth: '50px',
                            }}
                          >
                            <CloudUploadIcon fontSize="small" />
                          </Button>
                        </TableCell>
                        {/* <TableCell align="center" sx={cellStyle}>
                          {certificate.comman_layer_cert ? (
                            <Tooltip title="Download Common Layer Certificate">
                              <IconButton href={certificate.comman_layer_cert} target="_blank" rel="noopener noreferrer">
                                <CloudDownloadIcon color="primary" />
                              </IconButton>
                            </Tooltip>
                          ) : "N/A"}
                        </TableCell>
                        <TableCell align="center" sx={cellStyle}>
                          {certificate.fitment_cert ? (
                            <Tooltip title="Download Fitment Certificate">
                            <IconButton href={certificate.fitment_cert} target="_blank" rel="noopener noreferrer">
                            <CloudDownloadIcon color="primary" />
                            </IconButton>
                            </Tooltip>
                            ) : "N/A"}
                            </TableCell> */}
                        <TableCell align="center" sx={cellStyle}>{formatDateFromEpoch(certificate.common_layer_cert_issued_date)}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{formatDateFromEpoch(certificate.fitment_cert_issued_date)}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.project_name || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>
                          {certificate.request_type ? (
                            <Chip
                              label={certificate.request_type}
                              size="small"
                              sx={{
                                backgroundColor: `${requestTypeColor(certificate.request_type)}1A`,
                                color: requestTypeColor(certificate.request_type),
                                border: `1px solid ${requestTypeColor(certificate.request_type)}`,
                                fontWeight: 600,
                                height: 22,
                              }}
                            />
                          ) : "N/A"}
                        </TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.inProgressDate || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.device_model || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.device_serial_number || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.device_imei_number || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.engine_number || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.iccid_number || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.customer_name || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.customer_number || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.customer_otp_number || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.customer_address || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.vehicle_model || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.registration_state || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.rto_name || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.rto_number || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.dealer_name || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.dealer_contact_number || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.alternate_dealer_name || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.alternate_dealer_number || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.paymentStatus || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.sim_activation_status || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.msisdn_1 || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.msisdn_2 || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.vehicle_type || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.year_of_manufacture || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>
                          {certificate.requestStatus ? (
                            <Chip
                              label={certificate.requestStatus}
                              size="small"
                              sx={{
                                backgroundColor: `${statusColor(certificate.requestStatus)}1A`,
                                color: statusColor(certificate.requestStatus),
                                border: `1px solid ${statusColor(certificate.requestStatus)}`,
                                fontWeight: 600,
                                height: 22,
                              }}
                            />
                          ) : "N/A"}
                        </TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.otp || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.otpSentOn || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.reason || "N/A"}</TableCell>
                        {/* Modified Telematics Check column to show a button */}
                        <TableCell align="center" sx={cellStyle}>
                          <Button
                            variant="contained"
                            onClick={() => handleButtonClick('Telematics Check', certificate)}
                            sx={{
                              backgroundColor: '#58B80F1A',
                              color: '#58B80F',
                              borderRadius: '12px',
                              height: '24px',
                              padding: '0 12px',
                              fontSize: '12px',
                              fontWeight: 'normal',
                              textTransform: 'none',
                              boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                              '&:hover': {
                                backgroundColor: '#58B80F33',
                              },
                              border: '1px solid #58B80F',
                              minWidth: '108px',
                            }}
                          >
                            TELEMATICS CHECK
                          </Button>
                        </TableCell>
                        {/* Modified Assign To L2 column to show a button */}
                        <TableCell align="center" sx={cellStyle}>
                          <Button
                            variant="contained"
                            onClick={() => handleButtonClick('Assign To L2', certificate)}
                            sx={{
                              backgroundColor: '#58B80F1A',
                              color: '#58B80F',
                              borderRadius: '12px',
                              height: '24px',
                              padding: '0 12px',
                              fontSize: '12px',
                              fontWeight: 'normal',
                              textTransform: 'none',
                              boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                              '&:hover': {
                                backgroundColor: '#58B80F33',
                              },
                              border: '1px solid #58B80F',
                              minWidth: '108px',
                            }}
                          >
                            ASSIGN
                          </Button>
                        </TableCell>
                        {/* Queue actions: Park/Close on live rows, Unpark/Close on parked rows */}
                        <TableCell align="center" sx={cellStyle}>
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', flexWrap: 'nowrap' }}>
                            {certificate.requestStatus === 'Parked' ? (
                              <Button
                                size="small"
                                variant="outlined"
                                color="secondary"
                                disabled={actionBusy}
                                onClick={() => handleUnpark(certificate)}
                                sx={{ borderRadius: '12px', height: '24px', fontSize: '11px', textTransform: 'none', minWidth: 'auto', px: 1.2 }}
                              >
                                Unpark
                              </Button>
                            ) : certificate.requestStatus !== 'Closed' ? (
                              <Button
                                size="small"
                                variant="outlined"
                                color="secondary"
                                disabled={actionBusy}
                                onClick={() => openParkDialog(certificate)}
                                sx={{ borderRadius: '12px', height: '24px', fontSize: '11px', textTransform: 'none', minWidth: 'auto', px: 1.2 }}
                              >
                                Park
                              </Button>
                            ) : null}
                            {certificate.requestStatus !== 'Closed' && (
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                disabled={actionBusy}
                                onClick={() => handleClose(certificate)}
                                sx={{ borderRadius: '12px', height: '24px', fontSize: '11px', textTransform: 'none', minWidth: 'auto', px: 1.2 }}
                              >
                                Close
                              </Button>
                            )}
                            {/*
                              A closed request had no action at all — the queue
                              could close one by mistake and never take it back.
                              SUPER_ADMIN gets Reopen here; everyone else still
                              sees the dash, and the server enforces the same rule.
                            */}
                            {certificate.requestStatus === 'Closed' && (
                              canReopen ? (
                                <Tooltip
                                  title="Reopen — puts this request back in the live queue. Only works if it was closed from here, not by a KYC approval or rejection."
                                  placement="top"
                                  arrow
                                >
                                  <span>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      disabled={actionBusy}
                                      onClick={() => handleReopen(certificate)}
                                      startIcon={<RestoreIcon sx={{ fontSize: 14 }} />}
                                      sx={{
                                        borderRadius: '12px', height: '24px', fontSize: '11px',
                                        textTransform: 'none', minWidth: 'auto', px: 1.2,
                                        color: '#E60012', borderColor: '#E60012',
                                        '&:hover': { borderColor: '#c1000f', backgroundColor: '#E600120A' },
                                      }}
                                    >
                                      Reopen
                                    </Button>
                                  </span>
                                </Tooltip>
                              ) : (
                                <Typography sx={{ fontSize: '12px', color: '#888' }}>—</Typography>
                              )
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>

        {/* Pagination */}
        <Box sx={{
          position: 'sticky',
          bottom: 0,
          zIndex: 20,
          background: '#fff',
          borderTop: '1px solid #e0e0e0',
          pt: 2,
          mt: 'auto',
          overflowX: 'auto',
        }}>
          <TablePagination
            rowsPerPageOptions={[20, 50, 100, 500]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Rows per page:"
            sx={{
              minWidth: '600px',
              '.MuiTablePagination-toolbar': {
                flexWrap: 'nowrap',
                justifyContent: { xs: 'center', sm: 'space-between' },
              },
            }}
          />
        </Box>
      </Card>

      {/* Common Layer Upload Dialog */}
      <CommonLayerUpload
        open={commonLayerDialogOpen}
        onClose={handleCloseCommonLayerDialog}
        onUpload={handleCommonLayerFileUpload}
        uploadPdfMutation={handleUploadPdf}
        uploadCertificateMutation={handleUploadCertificate}
        kycId={selectedCertificate?.kyc_id || ''}
      />

      {/* Fitment Upload Dialog */}
      <FitmentUpload
        open={fitmentDialogOpen}
        onClose={handleCloseFitmentDialog}
        onUpload={handleFitmentFileUpload}
        uploadPdfMutation={handleUploadPdf}
        uploadCertificateMutation={handleUploadCertificate}
        kycId={selectedCertificate?.kyc_id || ''}
        certificate={selectedCertificate}
      />

      {/* Assign To L2 Dialog */}
      <AssignToL2
        open={assignToL2DialogOpen}
        onClose={handleAssignToL2DialogClose}
        onSubmit={handleAssignToL2Submit}
        certificate={selectedCertificate}
      />
      <UpdateRequestDialog
        open={updateDialogOpen}
        onClose={handleUpdateDialogClose}
        onSubmit={handleUpdateRequestSubmit}
        requestId={selectedCertificate?.request_id || ""}
        vin={selectedCertificate?.vin || ""}
      />

      {/* Park dialog: reason + follow-up date, same as the partner console */}
      <Dialog open={parkDialogOpen} onClose={() => setParkDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Park request {parkTarget ? `(${parkTarget.request_id || parkTarget.vin})` : ''}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              select
              label="Reason"
              size="small"
              fullWidth
              value={parkReason}
              onChange={(e) => setParkReason(e.target.value)}
            >
              {PARK_REASONS.map((r) => (
                <MenuItem key={r} value={r}>{r}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Follow-up date"
              type="date"
              size="small"
              fullWidth
              value={parkFollowUpDate}
              onChange={(e) => setParkFollowUpDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setParkDialogOpen(false)} disabled={actionBusy}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleParkSubmit} disabled={actionBusy}>
            Park
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default CertificateManager;